import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Table,
  Tag,
  Space,
  Typography,
  Spin,
  Alert,
  App,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMaterialIssuance } from '../../hooks/useMaterialIssuance';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useUserDisplayResolver } from '../../hooks/useUserDisplayResolver';
import { PrintableMaterialIssueVoucher } from '@/components/manufacturing/PrintableMaterialIssueVoucher';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { documentLinePieces, documentLineSize } from '@/utils/inventory/measurementQuantity';
import materialIssuanceService from '@/services/materialIssuance.service';
import api from '@/services/api.service';
import { PrintableVoucher } from '@/components/sales-invoice/PrintableVoucher';
import { MtcDocumentLink } from '@/components/documents/MtcDocumentLink';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_MATERIAL_ISSUANCE } from '@/constants/approval-entity-types';
import { useQueryClient } from '@tanstack/react-query';

const { Title, Text } = Typography;

const MaterialIssueDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const { resolve } = useUserDisplayResolver();
  const { isCriticalCode } = useCriticalItemLookup();
  const [printingVoucher, setPrintingVoucher] = useState(false);
  const [printingJv, setPrintingJv] = useState(false);

  const { data: issuance, isLoading, isError, refetch } = useMaterialIssuance(id || '', {
    enabled: !!id,
  });
  const { data: departments = [] } = useDepartmentsForSelection();
  const showUniversalApproval = !!issuance?.approval_instance_id;

  const handlePrintVoucher = useCallback(async () => {
    if (!id) return;
    try {
      setPrintingVoucher(true);
      const full = await materialIssuanceService.getById(id);
      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(<PrintableMaterialIssueVoucher issuance={full} onClose={cleanup} />);
      window.addEventListener('afterprint', cleanup);
      setTimeout(() => window.print(), 300);
    } catch (e: unknown) {
      message.error((e as Error)?.message || 'Failed to print voucher');
    } finally {
      setPrintingVoucher(false);
    }
  }, [id, message]);

  const handlePrintJv = useCallback(async () => {
    if (!issuance?.journal_voucher_id) {
      message.warning('No accounting voucher linked.');
      return;
    }
    try {
      setPrintingJv(true);
      const voucherRes = await api.get(`/finance/vouchers/${issuance.journal_voucher_id}`);
      const voucher = (voucherRes as { data?: { data?: unknown } })?.data?.data ?? voucherRes?.data;
      if (!voucher || !(voucher as { id?: string }).id) {
        message.warning('Accounting voucher not found.');
        return;
      }
      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(
        <PrintableVoucher voucher={voucher as never} referenceLabel="Material Issue" onClose={cleanup} />
      );
      window.addEventListener('afterprint', cleanup);
      setTimeout(() => window.print(), 300);
    } catch (e: unknown) {
      message.error((e as Error)?.message || 'Failed to print JV');
    } finally {
      setPrintingJv(false);
    }
  }, [issuance, message]);

  if (!id) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Missing issuance id" />
      </div>
    );
  }

  const lineColumns: ColumnsType<NonNullable<typeof issuance>['items'][number]> = [
    {
      title: 'Item',
      key: 'item',
      render: (_, row) => (
        <ItemCodeWithCritical
          code={row.item_name || row.item_code || row.item_id}
          critical={isCriticalCode(row.item_code)}
        />
      ),
    },
    {
      title: 'BOQ line',
      key: 'boq_line',
      ellipsis: true,
      render: (_, row) => row.boq_line_label || '—',
    },
    {
      title: 'Size (L×W×H)',
      key: 'size',
      render: (_, row) => documentLineSize(row) || '—',
    },
    {
      title: 'Pcs',
      key: 'pcs',
      align: 'right',
      render: (_, row) => documentLinePieces(row) ?? '—',
    },
    {
      title: 'Qty',
      dataIndex: 'issued_quantity',
      align: 'right',
      render: (v: number) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }),
    },
    { title: 'UOM', dataIndex: 'uom', width: 80 },
    {
      title: 'Unit cost',
      dataIndex: 'unit_cost',
      align: 'right',
      render: (v: number) => formatCurrency(Number(v || 0)),
    },
    {
      title: 'Total',
      dataIndex: 'total_cost',
      align: 'right',
      render: (v: number) => formatCurrency(Number(v || 0)),
    },
    {
      title: 'Heat',
      dataIndex: 'heat_number',
      render: (v: string) => v || '—',
    },
    {
      title: 'Dimension',
      dataIndex: 'dimension',
      render: (v: string) => v || '—',
    },
    {
      title: 'MTC',
      key: 'mtc',
      render: (_, row) => (
        <MtcDocumentLink
          documentId={row.mtc_document_id}
          documentNumber={row.mtc_document_number}
          compact
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/inventory/material-issues')}>
          Back to Material Issues
        </Button>
      </Space>

      {isLoading ? (
        <Spin size="large" />
      ) : isError || !issuance ? (
        <Alert type="error" message="Material issuance not found" />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Title level={2} style={{ margin: 0 }}>
              {issuance.issuance_number}{' '}
              {issuance.issuance_status ? <Tag>{issuance.issuance_status}</Tag> : null}
              {issuance.approval_status ? <Tag color="blue">{issuance.approval_status}</Tag> : null}
            </Title>
            <Space>
              <Button icon={<PrinterOutlined />} loading={printingVoucher} onClick={handlePrintVoucher}>
                Print voucher
              </Button>
              <ReportDocumentActions
                reportKey="material-issue"
                params={{ id: issuance.id }}
                documentId={issuance.id}
                sendPermissions={['inventory.material_issue.create']}
              />
              {issuance.journal_voucher_id ? (
                <Button icon={<PrinterOutlined />} loading={printingJv} onClick={handlePrintJv}>
                  Print JV
                </Button>
              ) : null}
            </Space>
          </div>

          {showUniversalApproval ? (
            <Card style={{ marginBottom: 16 }}>
              <ApprovalPanel
                entityType={ENTITY_TYPE_MATERIAL_ISSUANCE}
                entityId={issuance.id}
                onApproveViaDocument={async (remarks) => {
                  await materialIssuanceService.approve(issuance.id, remarks);
                  await refetch();
                  queryClient.invalidateQueries({ queryKey: ['material-issuances'] });
                }}
                onRejectViaDocument={async (payload) => {
                  await materialIssuanceService.rejectApproval(issuance.id, payload);
                  await refetch();
                  queryClient.invalidateQueries({ queryKey: ['material-issuances'] });
                }}
                extraInvalidateQueryKeys={[['material-issuances', id], ['material-issuances']]}
              />
            </Card>
          ) : null}

          <Card>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="Material request">
                <Link to={`/scm/inventory/material-requests/${issuance.material_request_id}`}>
                  {issuance.materialRequest?.request_number || issuance.material_request_id}
                </Link>
              </Descriptions.Item>
              <Descriptions.Item label="Department">
                {(() => {
                  const deptId =
                    issuance.department_id ||
                    (issuance.materialRequest as { department_id?: string } | undefined)?.department_id;
                  if (!deptId) return '—';
                  const d = departments.find((x) => x.id === deptId);
                  return d ? `${d.department_code} - ${d.department_name}` : deptId;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Work order">
                {issuance.work_order_number ? (
                  <Link to={`/mfg/work-orders/${issuance.work_order_id}`}>
                    {issuance.work_order_number}
                  </Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Project">
                {issuance.project_summary
                  ? `${issuance.project_summary.project_number || ''} ${issuance.project_summary.project_name || ''}`.trim()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Issuance date">
                {issuance.issuance_date
                  ? dayjs(issuance.issuance_date).format('DD MMM YYYY HH:mm')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag>{issuance.issuance_type}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Total cost">
                {formatCurrency(Number(issuance.total_cost || 0))}
              </Descriptions.Item>
              <Descriptions.Item label="Warehouse">
                {issuance.source_warehouse_summary?.warehouse_name ||
                  issuance.source_warehouse_summary?.warehouse_code ||
                  issuance.source_warehouse_id}
              </Descriptions.Item>
              <Descriptions.Item label="Issued by">
                {issuance.issued_by_name || resolve(issuance.issued_by, '—')}
              </Descriptions.Item>
              <Descriptions.Item label="Received by">
                {issuance.received_by_name || resolve(issuance.received_by || '', '—')}
              </Descriptions.Item>
              <Descriptions.Item label="JV">
                {issuance.journal_voucher_summary?.voucher_number || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Issued lines" style={{ marginTop: 16 }}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={issuance.items || []}
              columns={lineColumns}
              rowClassName={(row) =>
                getCriticalItemRowClassName({ is_critical: isCriticalCode(row.item_code) })
              }
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default MaterialIssueDetailPage;
